import { useState, useEffect, useMemo } from 'react';
import { useAppContext } from '@/App';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Settings, Settings2, ChevronDown, GraduationCap, BookOpen } from 'lucide-react';
import { buildNavItems, isNavPathActive } from '@/lib/navigation';
import { getMyCourseNav, getNavigationLayout } from '@/lib/api';
import { ADMIN_SCOPES, hasAdminScope } from '@/lib/auth';
import NavigationEditor from './NavigationEditor';

export default function Sidebar({ onNavigate }) {
  const { role } = useAppContext();
  const { profile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [navigationLayout, setNavigationLayout] = useState({ items: null, revision: 0 });
  const [editorLayout, setEditorLayout] = useState(null);
  const [loadingEditor, setLoadingEditor] = useState(false);
  const [navigationError, setNavigationError] = useState('');
  const canCustomize = role === 'admin' && hasAdminScope(profile, ADMIN_SCOPES.ADMIN);
  const navItems = useMemo(() => buildNavItems(role, profile, navigationLayout.items), [role, profile, navigationLayout]);

  useEffect(() => {
    setEditorLayout(null);
    setNavigationLayout({ items: null, revision: 0 });
    if (role !== 'admin') return undefined;
    let cancelled = false;
    const refresh = () => getNavigationLayout().then(({ data }) => {
      if (!cancelled) { setNavigationLayout(data); setNavigationError(''); }
    }).catch(() => { /* Keep the current/default menu usable when settings are unavailable. */ });
    refresh();
    window.addEventListener('focus', refresh);
    return () => { cancelled = true; window.removeEventListener('focus', refresh); };
  }, [role, profile?.id]);

  const openEditor = async () => {
    setLoadingEditor(true); setNavigationError('');
    try {
      const { data } = await getNavigationLayout();
      setNavigationLayout(data); setEditorLayout(data);
    } catch {
      setNavigationError('Could not load navigation settings. Please try again.');
    } finally { setLoadingEditor(false); }
  };

  const isPathActive = (path) => isNavPathActive(path, location.pathname);

  const [openGroups, setOpenGroups] = useState({});
  // The cohort tree is data, not a static list: which intakes appear depends on
  // which courses this person teaches or is enrolled in.
  const [intakeGroups, setIntakeGroups] = useState([]);

  useEffect(() => {
    if (!role || role === 'agency') return;
    let cancelled = false;
    getMyCourseNav()
      .then(({ data }) => { if (!cancelled) setIntakeGroups(data?.groups || []); })
      .catch(() => { if (!cancelled) setIntakeGroups([]); });
    return () => { cancelled = true; };
  }, [role]);

  // Open the intake that holds the course being viewed.
  useEffect(() => {
    intakeGroups.forEach((group) => {
      if (group.courses?.some((course) => isPathActive(course.path))) {
        setOpenGroups((prev) => (prev[`intake:${group.id}`] ? prev : { ...prev, [`intake:${group.id}`]: true }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intakeGroups, location.pathname]);

  // Auto-expand a group when the current route is one of its children.
  useEffect(() => {
    navItems.forEach((item) => {
      if (item.children && item.children.some((c) => isPathActive(c.path))) {
        const key = item.id || item.label;
        setOpenGroups((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, role, navItems]);

  const toggleGroup = (label) =>
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));

  const handleClick = (path) => {
    navigate(path);
    if (onNavigate) onNavigate();
  };

  return (
    <div className="flex flex-col h-full bg-[#062B1A] text-white w-full">
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 h-14 flex-shrink-0 border-b border-white/10">
        <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center flex-shrink-0 ring-1 ring-[#D4AF37]/40">
          <img src="/assets/mcc-logo-outlined.png" alt="MCC" className="w-7 h-7 object-contain" />
        </div>
        <span className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'var(--font-display)' }}>
          MCC Portal
        </span>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        <div className="space-y-1">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/40 px-3 mb-2">
            {role === 'admin' ? 'Administration' : role === 'instructor' ? 'Instructor' : role === 'agency' ? 'Agency' : role === 'student' ? 'Learning' : 'Session'}
          </p>
          {navItems.map((item) => {
            if (item.children) {
              const childActive = item.children.some((c) => isPathActive(c.path));
              const groupKey = item.id || item.label;
              const expanded = !!openGroups[groupKey];
              return (
                <div key={groupKey}>
                  <button
                    type="button"
                    data-testid={`sidebar-group-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => toggleGroup(groupKey)}
                    aria-expanded={expanded}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                      childActive
                        ? 'bg-[#D4AF37]/20 text-[#D4AF37]'
                        : 'text-white/70 hover:text-white hover:bg-white/8'
                    }`}
                  >
                    <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
                    <span className="flex-1 text-left">{item.label}</span>
                    <ChevronDown
                      className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {expanded && (
                    <div className="mt-1 space-y-1 ml-4 pl-3 border-l border-white/10">
                      {item.children.map((child) => {
                        const isActive = isPathActive(child.path);
                        return (
                          <button
                            key={child.path}
                            data-testid={`sidebar-nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                            onClick={() => handleClick(child.path)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                              isActive
                                ? 'bg-[#D4AF37]/20 text-[#D4AF37]'
                                : 'text-white/70 hover:text-white hover:bg-white/8'
                            }`}
                          >
                            <child.icon className="w-[18px] h-[18px] flex-shrink-0" />
                            <span>{child.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            const isActive = isPathActive(item.path);
            return (
              <button
                key={item.path}
                data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => handleClick(item.path)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                  isActive
                    ? 'bg-[#D4AF37]/20 text-[#D4AF37]'
                    : 'text-white/70 hover:text-white hover:bg-white/8'
                }`}
              >
                <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
                <span>{item.label}</span>
              </button>
            );
          })}
          {intakeGroups.length > 0 && (
            <div className="pt-4">
              <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/40">
                Intakes
              </p>
              {intakeGroups.map((group) => {
                const key = `intake:${group.id}`;
                const expanded = !!openGroups[key];
                const childActive = group.courses?.some((course) => isPathActive(course.path));
                return (
                  <div key={key}>
                    <button
                      type="button"
                      data-testid={`sidebar-intake-${group.id}`}
                      onClick={() => toggleGroup(key)}
                      aria-expanded={expanded}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                        childActive
                          ? 'bg-[#D4AF37]/20 text-[#D4AF37]'
                          : 'text-white/70 hover:text-white hover:bg-white/8'
                      }`}
                    >
                      <GraduationCap className="w-[18px] h-[18px] flex-shrink-0" />
                      <span className="flex-1 text-left truncate" title={group.label}>{group.label}</span>
                      <ChevronDown
                        className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {expanded && (
                      <div className="mt-1 space-y-1 ml-4 pl-3 border-l border-white/10">
                        {group.courses.map((course) => {
                          const isActive = isPathActive(course.path);
                          return (
                            <button
                              key={course.id}
                              onClick={() => handleClick(course.path)}
                              title={course.title || course.label}
                              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150 ${
                                isActive
                                  ? 'bg-[#D4AF37]/20 text-[#D4AF37]'
                                  : 'text-white/70 hover:text-white hover:bg-white/8'
                              }`}
                            >
                              <BookOpen className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate">{course.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {navItems.length === 0 && (
            <div className="px-3 py-4 text-sm text-white/50 leading-relaxed">
              Finish signing in to load the correct workspace.
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Footer */}
      <div className="p-4 border-t border-white/10">
        {canCustomize && <button type="button" onClick={openEditor} disabled={loadingEditor}
          className="mb-3 flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-xs text-white/80 hover:bg-white/10 disabled:opacity-50">
          <Settings2 className="size-4" />{loadingEditor ? 'Loading navigation…' : 'Customize navigation'}
        </button>}
        {navigationError && <p role="alert" className="mb-2 text-xs text-amber-200">{navigationError}</p>}
        <div className="flex items-center gap-2 text-white/50 text-xs">
          <Settings className="w-3.5 h-3.5" />
          <span>MCC Portal v1.0</span>
        </div>
      </div>
      {canCustomize && editorLayout && <NavigationEditor layout={editorLayout} onClose={() => setEditorLayout(null)}
        onSaved={(data) => { setNavigationLayout(data); setEditorLayout(null); }} />}
    </div>
  );
}
